/** AI Gateway API client. */

const API_BASE = import.meta.env.VITE_AI_GATEWAY_URL || "";

function headers(): Record<string, string> {
  const h: Record<string, string> = { "content-type": "application/json" };
  const token = localStorage.getItem("infra_token");
  if (token) h["authorization"] = `Bearer ${token}`;
  return h;
}

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers: { ...headers(), ...(opts?.headers as Record<string, string> || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `API error ${res.status}`);
  }
  return res.json();
}

export async function chat(params: {
  messages: Array<{ role: string; content: any }>;
  session_id?: string;
  route_mode?: string;
  project?: string;
  overrides?: string[];
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  tools?: boolean | any[];
  approved_tool_ids?: string[];
}) {
  return api<any>("/v1/chat", { method: "POST", body: JSON.stringify(params) });
}

/** Streaming chat — calls /v1/chat/stream and returns an EventSource-like reader */
export async function chatStream(
  params: Parameters<typeof chat>[0],
  onEvent: (event: string, data: any) => void,
): Promise<void> {
  const res = await fetch(`${API_BASE}/v1/chat/stream`, {
    method: "POST",
    credentials: "include",
    headers: headers(),
    body: JSON.stringify(params),
  });

  if (!res.ok || !res.body) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error || `Stream error ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from buffer
    const lines = buffer.split("\n");
    buffer = lines.pop() || ""; // keep incomplete line in buffer

    let currentEvent = "message";
    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          onEvent(currentEvent, data);
        } catch {}
      }
    }
  }
}

export async function listTools(caller = "ai") {
  return api<{ tools: any[] }>(`/v1/tools?caller=${caller}`);
}

export async function getTuningConfig() {
  return api<{ configs: any[] }>("/v1/admin/tuning");
}

export async function updateTuningConfig(config: Record<string, any>) {
  return api<any>("/v1/admin/tuning", { method: "PUT", body: JSON.stringify(config) });
}

export async function listModels() {
  return api<{ models: any[] }>("/v1/models");
}

export async function getPreferences() {
  return api<{ preferences: any }>("/v1/preferences");
}

export async function updatePreferences(prefs: Record<string, any>) {
  return api<{ preferences: any }>("/v1/preferences", { method: "PUT", body: JSON.stringify(prefs) });
}

export async function getCost() {
  return api<{ daily_total_usd: number; monthly_total_usd: number }>("/v1/cost");
}

export async function listSessions(projectId?: string) {
  const qs = projectId ? `?project_id=${projectId}` : "";
  return api<{ sessions: any[] }>(`/v1/sessions${qs}`);
}

export async function createSession(caller = "ai", projectId?: string) {
  return api<{ session_id: string; project_id?: string }>("/v1/sessions", { method: "POST", body: JSON.stringify({ caller, project_id: projectId }) });
}

export async function getSession(id: string) {
  return api<{ session_id: string; messages: any[] }>(`/v1/sessions/${id}`);
}

export async function deleteSession(id: string) {
  return api<{ deleted: boolean }>(`/v1/sessions/${id}`, { method: "DELETE" });
}

export async function updateSession(id: string, data: { title?: string }) {
  return api<{ updated: boolean }>(`/v1/sessions/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

// ── Realms API ──────────────────────────────────────────────────────────────────

export async function listRealms() {
  return api<{ realms: any[] }>("/v1/realms");
}

export async function createRealm(data: { name: string; type: "personal" | "business"; description?: string; color?: string; icon?: string }) {
  return api<{ id: string; name: string; type: string; is_default: number }>("/v1/realms", { method: "POST", body: JSON.stringify(data) });
}

export async function updateRealm(id: string, data: any) {
  return api<any>(`/v1/realms/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteRealm(id: string) {
  return api<any>(`/v1/realms/${id}`, { method: "DELETE" });
}

export async function getRealmSummary(id: string) {
  return api<any>(`/v1/realms/${id}/summary`);
}

export async function migrateDataToRealm(realmId: string) {
  return api<any>("/v1/realms/migrate", { method: "POST", body: JSON.stringify({ realm_id: realmId }) });
}

// ── Developer API (repos, projects, connections) ──────────────────────────────

export async function getDeveloperRepos() {
  return api<{ repos: any[] }>("/v1/developer/repos");
}

export async function addDeveloperRepo(data: { owner: string; name: string; access_level?: string; local_path?: string; description?: string }) {
  return api<any>("/v1/developer/repos", { method: "POST", body: JSON.stringify(data) });
}

export async function deleteDeveloperRepo(id: string) {
  return api<any>(`/v1/developer/repos/${id}`, { method: "DELETE" });
}

export async function discoverRepos() {
  return api<{ discovered: any[]; total_github: number; already_registered: number }>("/v1/developer/repos/discover", { method: "POST" });
}

export async function getDeveloperProjects() {
  return api<{ projects: any[] }>("/v1/developer/projects");
}

export async function createDeveloperProject(data: { name: string; primary_repo_id: string; secondary_repo_ids?: string[]; description?: string; color?: string }) {
  return api<any>("/v1/developer/projects", { method: "POST", body: JSON.stringify(data) });
}

export async function updateDeveloperProject(id: string, data: any) {
  return api<any>(`/v1/developer/projects/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteDeveloperProject(id: string) {
  return api<any>(`/v1/developer/projects/${id}`, { method: "DELETE" });
}

export async function getDeveloperConnections() {
  return api<{ connections: any[] }>("/v1/developer/connections");
}

export async function connectGitHub(accessToken: string) {
  return api<{ connected: boolean; username: string; avatar_url: string }>("/v1/developer/connections/github", { method: "POST", body: JSON.stringify({ access_token: accessToken }) });
}

export async function testGitHubConnection() {
  return api<{ connected: boolean; username?: string; avatar_url?: string; rate_limit?: any; error?: string }>("/v1/developer/connections/github/test");
}

export async function disconnectDeveloper(provider: string) {
  return api<any>(`/v1/developer/connections/${provider}`, { method: "DELETE" });
}

// ── Ops dashboard API ─────────────────────────────────────────────────────────

export async function getOpsStatus() {
  return api<any>("/ops/status");
}

export async function getSpiceRouterOps() {
  return api<any>("/ops/spicerouter");
}

export async function getTestHistory(limit = 200) {
  return api<any>(`/ops/test/history?limit=${limit}`);
}

export async function getTestTrends(hours = 24) {
  return api<any>(`/ops/test/history/trends?hours=${hours}`);
}

export async function getTestRun(id: string) {
  return api<any>(`/ops/test/history/${id}`);
}

export async function getBackupStatus() {
  return api<any>("/ops/backup");
}

export async function getSecrets() {
  return api<any>("/ops/secrets");
}

export async function triggerTestRun() {
  return api<any>("/ops/test/run", { method: "POST" });
}

export async function triggerBackup() {
  return api<any>("/ops/backup/run", { method: "POST" });
}

// ── Skills API ─────────────────────────────────────────────

export async function listSkills(caller = "ai") {
  return api<{ skills: any[] }>(`/v1/skills?caller=${caller}`);
}

export async function activateSkill(skillId: string) {
  return api<{ ok: boolean; skill_id: string; activated: boolean }>(`/v1/skills/${skillId}/activate`, { method: "POST" });
}

export async function deactivateSkill(skillId: string) {
  return api<{ ok: boolean }>(`/v1/skills/${skillId}/activate`, { method: "DELETE" });
}

// ── Vaultwarden connection API ─────────────────────────────────────────────
export async function getVaultwardenStatus() {
  return api<{ connected: boolean; source: string; vw_url: string | null; client_id?: string; connected_at?: string; last_used_at?: string }>("/v1/vaultwarden/connect");
}

export async function connectVaultwarden(data: { vw_url: string; client_id: string; client_secret: string }) {
  return api<{ ok: boolean; vw_url: string; client_id: string }>("/v1/vaultwarden/connect", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function disconnectVaultwarden() {
  return api<{ ok: boolean; disconnected: boolean }>("/v1/vaultwarden/connect", { method: "DELETE" });
}
